/**
 * Azure AI Search Service
 * Handles querying pre-indexed documents from Azure AI Search with semantic ranking and vector search
 */

import type {
  AzureAISearchConfig,
  AISearchResponse,
  AISearchResult,
  AISearchQueryOptions,
  SearchMode,
} from '@/types/aiSearch.types';
import { getBackendUrl as getRuntimeBackendUrl } from '@/config/runtimeConfig';
import { generateQueryEmbedding } from './openAIService';
import { throwIfAborted, isUserCancellation } from '@/utils/cancellation';
import { getBackendHeaders } from './backendAuth';
import { getCurrentTraceId } from './webSearch/webSearchUtils';

/**
 * Get backend URL with priority:
 * 1. Runtime config (container startup injection - HIGHEST PRIORITY)
 * 2. Build-time env vars (development fallback)
 * 3. AdminContext localStorage (user override for testing only - LOWEST PRIORITY)
 */
function getBackendUrl(): string {
  // Priority 1 & 2: Runtime config (container env vars) > Build-time env vars
  const runtimeUrl = getRuntimeBackendUrl();
  if (runtimeUrl) {
    return runtimeUrl;
  }

  // Priority 3: AdminContext localStorage (fallback for testing/dev only)
  try {
    const adminConfigStr = localStorage.getItem('stellar_admin_config');
    if (adminConfigStr) {
      const adminConfig = JSON.parse(adminConfigStr);
      if (adminConfig.authDatabase?.backendUrl) {
        console.warn('[azureAISearchService] ⚠️ Using backend URL from localStorage (low priority):', adminConfig.authDatabase.backendUrl);
        return adminConfig.authDatabase.backendUrl;
      }
    }
  } catch (error) {
    console.warn('[azureAISearchService] Failed to load backend URL from admin config:', error);
  }

  // Final fallback
  return 'http://localhost:8000/api';
}

/**
 * Get authentication headers for the /api/ai-search proxy.
 *
 * Delegates to the shared backendAuth helper so SSO callers always
 * forward X-User-Email / X-User-Username (the backend needs them to
 * keep the users table out of the 'unknown' fallback state).
 *
 * When a chat turn is in flight (currentTraceId is set), the header
 * X-Trace-Id is included so the backend can correlate AI-search calls
 * to the parent trace.
 */
async function getAuthHeaders(): Promise<Record<string, string>> {
  const headers = await getBackendHeaders();
  const traceId = getCurrentTraceId();
  if (traceId) {
    return { ...headers, 'X-Trace-Id': traceId };
  }
  return headers;
}

/**
 * Internal type for Azure Search document response
 */
interface AzureSearchDocument {
  id?: string;
  '@search.documentId'?: string;
  content?: string;
  title?: string;
  url?: string;
  metadata?: Record<string, unknown>;
  '@search.score'?: number;
  '@search.captions'?: Array<{ text: string }>;
  '@search.highlights'?: { content?: string[] };
}

/**
 * Internal type for Azure Search API response
 */
interface AzureSearchApiResponse {
  value?: AzureSearchDocument[];
  '@odata.count'?: number;
}

/**
 * Search Azure AI Search index with query
 * Uses REST API for compatibility (Azure SDK can be added later for advanced features)
 */
export async function searchAIIndex(
  config: AzureAISearchConfig,
  query: string,
  options?: AISearchQueryOptions
): Promise<AISearchResponse> {
  if (!config.enabled) {
    throw new Error('Azure AI Search is not enabled for this agent');
  }

  if (!config.endpoint || !config.apiKey || !config.indexName) {
    throw new Error('Azure AI Search configuration is incomplete');
  }

  // Bail immediately if the user already pressed Stop before we started.
  throwIfAborted(options?.signal);

  const top = options?.top || config.top || 5;
  const semanticConfig = options?.semanticConfig || config.semanticConfig;
  const useVectorSearch = options?.useVectorSearch ?? config.useVectorSearch ?? false;

  // Determine search mode
  let searchMode: SearchMode = config.searchMode || 'hybrid';

  // If vector search is disabled, force keyword mode
  if (!useVectorSearch) {
    searchMode = 'keyword';
  }

  // Short-query heuristic: queries like "GDPR", "PCI compliance", "HIPAA",
  // or "ISO 27001" are dominated by exact-match terminology — semantic
  // matching adds little but the embedding round-trip costs ~430 ms.
  // When enabled, downgrade short queries (≤ 2 words after stripping
  // non-alphanumerics) to keyword-only mode.
  if (
    config.skipEmbeddingForShortQueries &&
    (searchMode === 'vector' || searchMode === 'hybrid')
  ) {
    const wordCount = query
      .replace(/[^\p{L}\p{N}\s]/gu, ' ')
      .trim()
      .split(/\s+/)
      .filter(Boolean).length;
    if (wordCount > 0 && wordCount <= 2) {
      console.log(
        `[AI Search] Short query heuristic: ${wordCount}-word query → keyword-only`
      );
      searchMode = 'keyword';
    }
  }

  console.log('[AI Search] Search mode:', searchMode);

  // Generate query embedding if needed (vector or hybrid mode)
  let queryVector: number[] | undefined = options?.queryVector;

  // When integrated vectorization is enabled the index has its own
  // vectorizer, so we skip the client-side embedding hop and let Azure
  // AI Search produce the query vector internally (~430 ms faster).
  const useIntegratedVectorization = !!config.useIntegratedVectorization;

  if (
    (searchMode === 'vector' || searchMode === 'hybrid') &&
    !queryVector &&
    !useIntegratedVectorization
  ) {
    // Check if embedding configuration is available
    if (config.embeddingEndpoint && config.embeddingApiKey && config.embeddingDeployment) {
      try {
        console.log('[AI Search] Generating query embedding...');

        queryVector = await generateQueryEmbedding(
          query,
          {
            endpoint: config.embeddingEndpoint,
            apiKey: config.embeddingApiKey,
            deployment: config.embeddingDeployment,
            model: config.embeddingModel,
            apiVersion: config.embeddingApiVersion || '2023-05-15',
            agentId: options?.agentId ?? null,
          },
          options?.signal
        );

        console.log(`[AI Search] Embedding generated: ${queryVector.length} dimensions`);
      } catch (error) {
        // A user cancel surfaces here as an AbortError — propagate it so the
        // turn aborts. Only genuine embedding failures degrade to keyword.
        if (isUserCancellation(error, options?.signal)) {
          throw error;
        }

        console.warn('[AI Search] Embedding generation failed, falling back to keyword search:', error);

        // Fallback to keyword search if embedding fails
        searchMode = 'keyword';
        queryVector = undefined;
      }
    } else {
      console.warn('[AI Search] Embedding configuration missing, falling back to keyword search');
      console.warn('[AI Search] Required: embeddingEndpoint, embeddingApiKey, embeddingDeployment');

      // Fallback to keyword search if no embedding config
      searchMode = 'keyword';
    }
  } else if (useIntegratedVectorization) {
    console.log('[AI Search] Using integrated vectorization (server-side embedding)');
  }

  try {
    // Use backend proxy to avoid CORS issues
    const backendUrl = getBackendUrl();
    const authHeaders = await getAuthHeaders();

    console.log('[AI Search] Using backend URL:', backendUrl);

    // Get field name mappings with defaults
    const contentFieldName = config.contentFieldName || 'content';
    const titleFieldName = config.titleFieldName || 'title';
    const urlFieldName = config.urlFieldName || 'url';

    // Build select fields - use wildcard if not explicitly configured
    // This avoids errors when index doesn't have expected fields
    const selectFields = config.selectFields || '*';
    const searchFields = config.searchFields || `${contentFieldName},${titleFieldName}`;

    console.log('[AI Search] Field mappings:', {
      contentFieldName,
      titleFieldName,
      urlFieldName,
      selectFields,
      searchFields,
    });

    if (searchMode === 'hybrid' && queryVector) {
      console.log(`[AI Search] Executing hybrid search with ${queryVector.length}-dimensional vector`);
      console.log(`[AI Search] Hybrid weights: vector=${config.vectorWeight || 0.5}, keyword=${config.keywordWeight || 0.5}`);
    } else if (searchMode === 'vector' && queryVector) {
      console.log(`[AI Search] Executing vector-only search with ${queryVector.length}-dimensional vector`);
    } else {
      console.log('[AI Search] Executing keyword-only search');
    }

    const response = await fetch(`${backendUrl}/ai-search`, {
      method: 'POST',
      headers: authHeaders,
      body: JSON.stringify({
        endpoint: config.endpoint,
        apiKey: config.apiKey,
        indexName: config.indexName,
        query,
        agentId: options?.agentId ?? null,
        config: {
          top,
          useSemanticRanking: !!semanticConfig,
          semanticConfig,
          useVectorSearch,
          searchMode,
          useIntegratedVectorization,
          vector: queryVector,
          vectorK: top,
          vectorFieldName: config.vectorFieldName,
          vectorWeight: config.vectorWeight,
          keywordWeight: config.keywordWeight,
          select: selectFields,
          searchFields: searchFields,
          // Forwarded so the backend's per-doc telemetry (search_event_documents)
          // can extract docUrl/docName from the same raw fields the chat UI
          // resolves — required for the dashboard's "Most accessed documents"
          // grid to render clickable links and to bucket distinct docs
          // correctly on indexes that don't expose `metadata_storage_path`.
          urlFieldName,
          titleFieldName,
          // Integrated vectorization (kind:'text' vector queries) needs
          // the 2024-07-01 API. External vectors still work fine on the
          // older default.
          apiVersion: useIntegratedVectorization ? '2024-07-01' : '2023-11-01',
          filter: options?.filter,
          orderBy: options?.orderBy,
        },
      }),
      signal: options?.signal,
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[AI Search] Query failed:', response.status, errorText);
      throw new Error(
        `Azure AI Search query failed: ${response.status} ${response.statusText}`
      );
    }

    const data = (await response.json()) as AzureSearchApiResponse;

    // Map results to AISearchResult format using flexible field names
    const results: AISearchResult[] = (data.value || []).map((doc) => ({
      id: doc.id || doc['@search.documentId'] || '',
      content: doc[contentFieldName] || doc.content || '',
      title: doc[titleFieldName] || doc.title || 'Untitled',
      url: doc[urlFieldName] || doc.url,
      metadata: doc.metadata,
      score: doc['@search.score'] || 0,
      captions: doc['@search.captions']?.map((c) => c.text),
      highlights: doc['@search.highlights']?.[contentFieldName] || doc['@search.highlights']?.content,
    }));

    console.log(`[AI Search] Retrieved ${results.length} results`);

    return {
      results,
      count: data['@odata.count'] || results.length,
    };
  } catch (error) {
    console.error('[AI Search] Search error:', error);
    throw error instanceof Error
      ? error
      : new Error('Failed to query Azure AI Search');
  }
}

/**
 * Build context string from AI Search results for LLM
 * ENHANCED: Increased content limits and better formatting for quality responses
 */
export function buildContextFromAISearchResults(
  results: AISearchResult[],
  maxContextLength: number = 128000 // Massive context window for GPT-5 (128k chars ≈ 32k tokens)
): string {
  if (results.length === 0) {
    return '';
  }

  let context = 'Azure AI Search results:\n\n';
  let currentLength = context.length;

  for (let i = 0; i < results.length; i++) {
    const result = results[i];

    // ENHANCED: Use full captions if available, otherwise use more content (2000 chars instead of 500)
    // Semantic captions are AI-generated highlights specifically relevant to the query
    let contentSnippet: string;
    if (result.captions && result.captions.length > 0) {
      // Use full semantic captions (they're already optimized)
      contentSnippet = result.captions.join(' ');
      console.log(`[AI Search] Using semantic captions for Document ${i + 1} (${contentSnippet.length} chars)`);
    } else {
      // Fallback to larger content excerpt
      contentSnippet = result.content.substring(0, 2000); // Increased from 500 to 2000
      console.log(`[AI Search] Using content excerpt for Document ${i + 1} (${contentSnippet.length} chars)`);
    }

    const docSection = `
[Doc ${i + 1}] (Relevance Score: ${result.score.toFixed(2)})
Title: ${result.title}
${result.url ? `URL: ${result.url}\n` : ''}Content: ${contentSnippet}${contentSnippet.length >= 2000 ? '...' : ''}
---
`;

    // Check if adding this document would exceed max length
    if (currentLength + docSection.length > maxContextLength) {
      console.log(
        `[AI Search] Context truncated at ${i} documents (max length: ${maxContextLength} chars)`
      );
      break;
    }

    context += docSection;
    currentLength += docSection.length;
  }

  console.log(`[AI Search] Built context: ${currentLength} chars from ${results.length} documents`);

  return context;
}

/**
 * Extract citations from AI Search results
 */
export function extractCitationsFromAISearch(
  results: AISearchResult[]
): Array<{
  id: string;
  title: string;
  url: string;
  snippet: string;
}> {
  return results.slice(0, 5).map((result) => ({
    id: result.id,
    title: result.title,
    url: result.url || '#',
    snippet:
      result.captions?.[0] || result.highlights?.[0] || result.content.substring(0, 200),
  }));
}

/**
 * Validate Azure AI Search configuration
 */
export function validateAISearchConfig(config: Partial<AzureAISearchConfig>): string[] {
  const errors: string[] = [];

  if (!config.endpoint) {
    errors.push('Azure AI Search endpoint is required');
  } else if (!config.endpoint.includes('search.windows.net')) {
    errors.push('Invalid Azure AI Search endpoint URL');
  }

  if (!config.apiKey) {
    errors.push('Azure AI Search API key is required');
  }

  if (!config.indexName) {
    errors.push('Azure AI Search index name is required');
  }

  if (config.top && (config.top < 1 || config.top > 50)) {
    errors.push('Top results must be between 1 and 50');
  }

  if (config.useVectorSearch && !config.vectorFieldName) {
    errors.push('Vector field name is required when vector search is enabled');
  }

  return errors;
}

/**
 * Test Azure AI Search connection
 * Performs a simple query to verify the configuration is valid
 */
export async function testAISearchConnection(
  config: AzureAISearchConfig
): Promise<{ success: boolean; message: string }> {
  try {
    // Validate configuration first
    const errors = validateAISearchConfig(config);
    if (errors.length > 0) {
      return {
        success: false,
        message: `Configuration errors: ${errors.join(', ')}`,
      };
    }

    // Perform a simple test query
    const response = await searchAIIndex(config, 'test', { top: 1 });

    return {
      success: true,
      message: `Connection successful! Index contains ${response.count} documents.`,
    };
  } catch (error) {
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
}
